import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trophy, BarChart3, Trash2, Lock } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useFeatureFlag } from "@/contexts/FlagsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster, toast } from "sonner";

const PLATFORMS = [
    { key: "instagram", label: "Instagram", max: 150 },
    { key: "linkedin", label: "LinkedIn", max: 300 },
    { key: "tiktok", label: "TikTok", max: 80 },
];

export default function ABTesting() {
    const { user } = useAuth();
    const enabled = useFeatureFlag("abTesting");
    const [experiments, setExperiments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [platform, setPlatform] = useState("instagram");
    const [variants, setVariants] = useState(["", ""]);

    const load = async () => {
        try {
            const { data } = await api.get("/experiments");
            setExperiments(data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (enabled) load();
        else setLoading(false);
    }, [enabled]);

    const create = async () => {
        const cleaned = variants.map((v) => v.trim()).filter(Boolean);
        if (cleaned.length < 2) return toast.error("Provide at least 2 variants");
        setCreating(true);
        try {
            await api.post("/experiments/bio", { platform, variants: cleaned });
            setVariants(["", ""]);
            toast.success("Experiment started");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const recordEngagement = async (expId, idx, score) => {
        try {
            const { data } = await api.post(`/experiments/${expId}/record`, {
                variantIndex: idx,
                engagementScore: Number(score),
            });
            setExperiments((arr) => arr.map((e) => (e.id === expId ? data : e)));
            if (data.status === "completed") toast.success("Winner determined!");
        } catch {
            toast.error("Record failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this experiment?")) return;
        await api.delete(`/experiments/${id}`);
        load();
    };

    if (!enabled) {
        return (
            <Layout>
                <Toaster theme="light" position="top-center" />
                <div className="mb-8">
                    <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                        // EXPERIMENTS / A/B TESTING
                    </div>
                    <h1 className="font-syne text-4xl font-bold text-aura-text">A/B Testing Lab</h1>
                </div>
                <div className="glass p-12 text-center" data-testid="ab-locked">
                    <Lock className="w-10 h-10 text-aura-violet mx-auto mb-4" />
                    <h2 className="font-syne text-2xl font-bold text-aura-text mb-2">Pro feature</h2>
                    <p className="text-aura-body mb-5">
                        A/B Testing is available on the <strong className="text-aura-violet">Pro tier</strong> only. Upgrade to start running bio experiments.
                    </p>
                    <a href="/settings#upgrade" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple">
                        Upgrade to Pro
                    </a>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="mb-8">
                <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                    // EXPERIMENTS / A/B TESTING
                </div>
                <h1 className="font-syne text-4xl sm:text-5xl font-bold text-aura-text">
                    Test, measure, <span className="text-grad-violet">win</span>.
                </h1>
                <p className="text-aura-body mt-2">Publish 2–4 bio variants, record real engagement, let data pick the winner.</p>
            </div>

            {/* Create */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 mb-6" data-testid="ab-create-card">
                <div className="flex items-center gap-2 mb-4">
                    <Plus className="w-4 h-4 text-aura-violet" />
                    <h3 className="font-syne text-lg font-semibold text-aura-text">New experiment</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
                    <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-3 text-sm text-aura-text focus:border-aura-violet focus:outline-none" data-testid="ab-platform-select">
                        {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <div className="space-y-2">
                        {variants.map((v, i) => (
                            <input
                                key={i}
                                value={v}
                                placeholder={`Variant ${String.fromCharCode(65 + i)}`}
                                onChange={(e) => {
                                    const next = [...variants];
                                    next[i] = e.target.value;
                                    setVariants(next);
                                }}
                                maxLength={PLATFORMS.find((p) => p.key === platform).max}
                                className="w-full bg-aura-input border border-aura-violet/15 rounded-lg px-4 py-2.5 text-sm text-aura-text focus:border-aura-violet focus:outline-none"
                                data-testid={`ab-variant-${i}`}
                            />
                        ))}
                        <div className="flex justify-between items-center">
                            {variants.length < 4 ? (
                                <button onClick={() => setVariants([...variants, ""])} className="text-xs text-aura-violet hover:text-aura-glow transition flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> Add variant
                                </button>
                            ) : <span />}
                            <button
                                onClick={create}
                                disabled={creating}
                                className="px-5 py-2 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white text-sm font-dm-sans font-medium glow-violet-soft btn-ripple disabled:opacity-60"
                                data-testid="ab-create-btn"
                            >
                                {creating ? "…" : "Start experiment"}
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* List */}
            {loading ? (
                <div className="glass p-10 text-center"><div className="cyber-spinner mx-auto" /></div>
            ) : experiments.length === 0 ? (
                <div className="glass p-10 text-center text-aura-body">No experiments yet. Create one above.</div>
            ) : (
                <div className="space-y-4">
                    {experiments.map((exp) => (
                        <motion.div
                            key={exp.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="glass p-6"
                            data-testid={`exp-${exp.id}`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <span className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-violet font-semibold">{exp.platform}</span>
                                    <div className="flex items-center gap-2 mt-1">
                                        {exp.status === "completed" ? (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-aura-violet/10 text-aura-violet font-jetbrains-mono uppercase tracking-wider flex items-center gap-1">
                                                <Trophy className="w-3 h-3" /> Winner found
                                            </span>
                                        ) : (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-aura-cyan/10 text-aura-cyan font-jetbrains-mono uppercase tracking-wider flex items-center gap-1">
                                                <BarChart3 className="w-3 h-3" /> Running
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button onClick={() => remove(exp.id)} className="text-aura-muted hover:text-red-500 transition">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {exp.variants.map((v, i) => {
                                    const isWinner = exp.winner && exp.winner === v.content;
                                    return (
                                        <div
                                            key={i}
                                            className={`rounded-lg p-3 border ${
                                                isWinner
                                                    ? "border-aura-violet bg-aura-violet/5 glow-violet-soft"
                                                    : "border-aura-violet/10 bg-white"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted">
                                                    Variant {String.fromCharCode(65 + i)}
                                                </span>
                                                {isWinner && <Trophy className="w-3.5 h-3.5 text-aura-violet" />}
                                            </div>
                                            <p className="text-xs text-aura-text leading-relaxed mb-3 min-h-[60px]">{v.content}</p>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                                placeholder="Engagement score (0-100)"
                                                defaultValue={v.engagementScore ?? ""}
                                                onBlur={(e) => {
                                                    if (e.target.value !== "" && !exp.winner) {
                                                        recordEngagement(exp.id, i, e.target.value);
                                                    }
                                                }}
                                                className="w-full bg-aura-input border border-aura-violet/15 rounded px-2.5 py-1.5 text-xs text-aura-text focus:border-aura-violet focus:outline-none"
                                                data-testid={`exp-${exp.id}-record-${i}`}
                                                disabled={!!exp.winner}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </Layout>
    );
}

